/**
 * Reliable tap-to-open for ECharts on touch devices.
 *
 * ECharts only emits `click` when zrender's own gate lets it through, and
 * that gate is far tighter than a real finger:
 *
 *   - zrender/lib/Handler.js — the click is dropped unless the pointer
 *     moved no more than **4px** between press and release, AND the same
 *     graphic element is under both points.
 *   - zrender/lib/dom/HandlerProxy.js — on the touch-event path, a touch
 *     is only converted to a click if the finger lifts within **300ms**
 *     (TOUCH_CLICK_DELAY).
 *
 * Measured against the real build under mobile emulation: a tap with 6px
 * of drift is dropped, and so is a perfectly still tap held for 450ms.
 * That is the "sometimes the modal doesn't appear" report — whether a tap
 * registered depended on how steady and how quick it happened to be.
 *
 * `useCoarsePointer` (already set on every chart) does NOT help: it only
 * widens the hit test in findHover, it does not relax either gate.
 *
 * The fix is to stop relying on `click` alone. `mouseup` is dispatched by
 * zrender unconditionally — no distance, element or duration gate — and
 * carries the same params (seriesName, dataIndex, name). So we listen to
 * both and decide for ourselves what counts as a tap, using thresholds
 * sized for a thumb rather than a mouse.
 *
 * Binding both keeps every input type working:
 *   - mouse: no touch events fire, so the tap flag stays false and the
 *     ordinary `click` path handles it exactly as before;
 *   - touch: `mouseup` fires first and handles the pick, then the
 *     following `click` (if zrender even emits one) is suppressed so a
 *     single tap can never be handled twice;
 *   - scroll/swipe/pinch started on the chart: exceeds the slop or the
 *     touch count, so it is not a tap and nothing opens.
 */

/** How far a finger may drift and still count as a tap, not a drag. */
export const TAP_SLOP_PX = 16;
/** How long a finger may rest and still count as a tap, not a long-press. */
export const TAP_MAX_MS = 800;
/** Window after a touch-driven pick in which a synthetic click is ignored. */
const CLICK_SUPPRESS_MS = 700;

export interface TapBindableChart {
  on(event: string, handler: (params: unknown) => void): void;
  off(event: string, handler?: (params: unknown) => void): void;
}

/**
 * Route both `click` and touch-driven `mouseup` into one handler.
 *
 * @param chart  the ECharts instance
 * @param dom    the container the chart was initialised on
 * @param onPick called once per genuine click/tap, with the ECharts params
 * @returns cleanup that removes every listener this added
 */
export function bindChartTap(
  chart: TapBindableChart,
  dom: HTMLElement,
  onPick: (params: unknown) => void,
): () => void {
  chart.off('click');
  chart.off('mouseup');

  let startX = 0;
  let startY = 0;
  let startedAt = 0;
  let maxDist = 0;
  let multiTouch = false;
  let gestureWasTap = false;
  let suppressClickUntil = 0;

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      multiTouch = true;
      gestureWasTap = false;
      return;
    }
    multiTouch = false;
    gestureWasTap = false;
    maxDist = 0;
    startedAt = Date.now();
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (multiTouch || e.touches.length !== 1) return;
    const d = Math.hypot(e.touches[0].clientX - startX, e.touches[0].clientY - startY);
    if (d > maxDist) maxDist = d;
  };

  const onTouchEnd = () => {
    gestureWasTap = !multiTouch
      && maxDist <= TAP_SLOP_PX
      && Date.now() - startedAt <= TAP_MAX_MS;
  };

  // Capture phase: these must run before zrender's own touchend handler,
  // which is what dispatches the `mouseup` we react to below.
  dom.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
  dom.addEventListener('touchmove', onTouchMove, { capture: true, passive: true });
  dom.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });

  const handleMouseUp = (params: unknown) => {
    if (!gestureWasTap) return;
    // One pick per gesture, and swallow the click the same touch may
    // still produce.
    gestureWasTap = false;
    suppressClickUntil = Date.now() + CLICK_SUPPRESS_MS;
    onPick(params);
  };

  const handleClick = (params: unknown) => {
    if (Date.now() < suppressClickUntil) return;
    onPick(params);
  };

  chart.on('mouseup', handleMouseUp);
  chart.on('click', handleClick);

  return () => {
    dom.removeEventListener('touchstart', onTouchStart, true);
    dom.removeEventListener('touchmove', onTouchMove, true);
    dom.removeEventListener('touchend', onTouchEnd, true);
    chart.off('mouseup', handleMouseUp);
    chart.off('click', handleClick);
  };
}
